
import { PostgrestError, PostgrestSingleResponse } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { isDatabaseArray, isDatabaseRecord, toDatabaseValue } from "./type-guards";
import type { Database } from "@/integrations/supabase/types";

type TableName = keyof Database['public']['Tables'];

/**
 * Type-safe wrapper for Supabase select queries with proper error handling
 */
export async function safeSelect<T>(
  query: any, 
  errorMessage = "Failed to fetch data"
): Promise<T[] | null> {
  try {
    const { data, error } = await query;
    
    if (error) {
      console.error("Select error:", error);
      toast.error(errorMessage);
      return null;
    }
    
    if (data && isDatabaseArray(data)) {
      return data as T[];
    }
    
    return [];
  } catch (err) {
    console.error("Error in safeSelect:", err);
    toast.error(errorMessage);
    return null;
  }
}

/**
 * Type-safe wrapper for Supabase insert operations
 */
export async function safeInsert<T>(
  tableName: TableName,
  values: any,
  errorMessage = "Failed to create record"
): Promise<T | null> {
  try {
    const { data, error } = await supabase
      .from(tableName)
      .insert(toDatabaseValue(values))
      .select()
      .single();
    
    if (error) {
      console.error("Insert error:", error);
      toast.error(errorMessage);
      return null;
    }
    
    return data as T;
  } catch (err) {
    console.error("Error in safeInsert:", err);
    toast.error(errorMessage);
    return null;
  }
}

/**
 * Helper to safely get a maybeSingle result
 */
export async function safeMaybeSingle<T>(
  query: any,
  errorMessage = "Failed to fetch record"
): Promise<T | null> {
  try {
    const { data, error } = await query.maybeSingle();
    
    if (error) {
      console.error("Query error:", error);
      toast.error(errorMessage);
      return null;
    }
    
    if (data && isDatabaseRecord(data)) {
      return data as T;
    }
    
    return null;
  } catch (err) {
    console.error("Error in safeMaybeSingle:", err);
    toast.error(errorMessage);
    return null;
  }
}

/**
 * Helper to safely get a single result
 */
export async function safeSingle<T>(
  query: any,
  errorMessage = "Failed to fetch record"
): Promise<T | null> {
  try {
    const { data, error } = await query.single();
    
    if (error) {
      console.error("Query error:", error);
      toast.error(errorMessage);
      return null;
    }
    
    if (data && isDatabaseRecord(data)) {
      return data as T;
    }
    
    return null;
  } catch (err) {
    console.error("Error in safeSingle:", err);
    toast.error(errorMessage);
    return null;
  }
}

/**
 * Helper function to handle Supabase responses with proper error handling
 */
export function handleSupabaseResponse<T>(
  response: PostgrestSingleResponse<T>,
  errorMessage = "Operation failed"
): T | null {
  if (response.error) {
    console.error("Supabase error:", response.error);
    toast.error(errorMessage);
    return null;
  }
  return response.data;
}
